import {AssocTerm} from '../model/AssocTerm'
import {formatDateLocalSlashSeparator} from '../../../formatters/formatMoment'
import {Nullable} from '../../../app/TypeHelpers'
import React, {ReactElement} from 'react'
import {NoWrapSpan} from '../shared/AssocComponents'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {Box} from '@mui/material'
import {Room} from '../model/Room'
import {RESIDENT_STATUS} from '../../../CodeMaster'

export class AssocDirectorHelper {
    static getDisplayAssocTerm(assocTerm: Nullable<AssocTerm>): string {
        if (!assocTerm) {
            return '未設定の任期'
        }
        const displayStartDate = formatDateLocalSlashSeparator(assocTerm.assocTermStartDate)
        const displayEndDate = formatDateLocalSlashSeparator(assocTerm.assocTermEndDate)

        return `${assocTerm.assocTermName}（${displayStartDate} 〜 ${displayEndDate}）`
    }

    static getRoomNumberNode(roomList: Room[]): ReactElement {
        return <>{
            roomList.map((room, i) =>
                (room.ridgeName !== '' ?
                        <Box minWidth={'68px'} key={`room-${room.ridgeCd}${room.roomNumber}${i}`}>
                            <LeftRowFlexContainer>
                                <div>{room.ridgeName}</div>
                                <Box width={'8px'}/>
                                <NoWrapSpan>
                                    {room.roomNumber}
                                </NoWrapSpan>
                            </LeftRowFlexContainer>
                        </Box>
                        :
                        <LeftRowFlexContainer key={`room-${room.ridgeCd}${room.roomNumber}${i}`}>
                            <NoWrapSpan>
                                {room.roomNumber}
                            </NoWrapSpan>
                        </LeftRowFlexContainer>
                )
            )
        }</>
    }

    static getRoomNumberNodeByResidentStatus(roomList: Room[]): ReactElement {
        let unregistered = false
        let abolition = false
        let registered = false
        for (const room of roomList) {
            if (room.status === RESIDENT_STATUS.UNREGISTERED) {
                unregistered = true
            }
            if (room.status === RESIDENT_STATUS.ABOLITION) {
                abolition = true
            }
            if (room.status === RESIDENT_STATUS.REGISTERED ||
                room.status === RESIDENT_STATUS.BEFORE_OCCUPANCY) {
                registered = true
            }
        }
        if (unregistered) {
            return this.getRoomNumberNode(roomList)
        } else if (abolition && !registered) {
            const sortedAbolitionRoomList: Room[] = roomList
                .filter((room) => room.abolitionDatetime !== null)
                .sort((a, b) => {
                    if (a.abolitionDatetime === null || b.abolitionDatetime === null) {
                        return 0
                    }
                    if (a.abolitionDatetime === b.abolitionDatetime) {
                        return 0
                    }
                    return a.abolitionDatetime < b.abolitionDatetime ? 1 : -1
                })
            return <>{
                sortedAbolitionRoomList[0].ridgeName !== '' ?
                    <Box minWidth={'68px'}>
                        <LeftRowFlexContainer>
                            <s><div>{sortedAbolitionRoomList[0].ridgeName}</div></s>
                            <Box width={'8px'}/>
                            <NoWrapSpan>
                                <s>{sortedAbolitionRoomList[0].roomNumber}</s>
                            </NoWrapSpan>
                        </LeftRowFlexContainer>
                    </Box>
                    :
                    <LeftRowFlexContainer>
                        <NoWrapSpan>
                            <s>{sortedAbolitionRoomList[0].roomNumber}</s>
                        </NoWrapSpan>
                    </LeftRowFlexContainer>
            }</>
        } else if (registered) {
            const registerRoomList: Room[] = roomList
                .filter((room) => room.status !== RESIDENT_STATUS.ABOLITION)
            return this.getRoomNumberNode(registerRoomList)
        }
        return <></>
    }

    static getJoinedRoomNumberNodeByResidentStatus(roomList: Room[]): ReactElement {
        let unregistered = false
        let abolition = false
        let registered = false
        for (const room of roomList) {
            if (room.status === RESIDENT_STATUS.UNREGISTERED) {
                unregistered = true
            }
            if (room.status === RESIDENT_STATUS.ABOLITION) {
                abolition = true
            }
            if (room.status === RESIDENT_STATUS.REGISTERED ||
                room.status === RESIDENT_STATUS.BEFORE_OCCUPANCY) {
                registered = true
            }
        }
        if (unregistered) {
            return <>{
                roomList
                    .map(room => `${room.ridgeName ?
                        (room.ridgeName + ' ') : ''}${room.roomNumber}`)
                    .join(', ')
            }</>
        } else if (abolition && !registered) {
            const sortedAbolitionRoomList: Room[] = roomList
                .filter((room) => room.abolitionDatetime !== null)
                .sort((a, b) => {
                    if (a.abolitionDatetime === null || b.abolitionDatetime === null) {
                        return 0
                    }
                    if (a.abolitionDatetime === b.abolitionDatetime) {
                        return 0
                    }
                    return a.abolitionDatetime < b.abolitionDatetime ? 1 : -1
                })
            return <>{
                <s>{sortedAbolitionRoomList[0].ridgeName ?
                    (sortedAbolitionRoomList[0].ridgeName + ' ') :
                    ''}{sortedAbolitionRoomList[0].roomNumber}</s>
            }</>
        } else if (registered) {
            const registerRoomList: Room[] = roomList
                .filter((room) => room.status !== RESIDENT_STATUS.ABOLITION)
            return <>{
                registerRoomList
                    .map(room => `${room.ridgeName ?
                        (room.ridgeName + ' ') : ''}${room.roomNumber}`)
                    .join(', ')
            }</>
        }
        return <></>
    }

    static getDisplayUserNameByResidentStatus(
        familyName: Nullable<string>,
        firstName: Nullable<string>,
        roomList: Room[]): string {
        let unregistered = false
        for (const room of roomList) {
            if (room.status === RESIDENT_STATUS.UNREGISTERED) {
                unregistered = true
            }
        }
        if (unregistered) {
            return '（未登録）'
        } else {
            return familyName && firstName ? `${familyName} ${firstName}` : '（退会済）'
        }
    }

    static getDisplayUserNameNodeByResidentStatus(
        familyName: Nullable<string>,
        firstName: Nullable<string>,
        roomList: Room[]
    ): ReactElement {
        let unregistered = false
        for (const room of roomList) {
            if (room.status === RESIDENT_STATUS.UNREGISTERED) {
                unregistered = true
            }
        }
        if (unregistered) {
            return <NoWrapSpan>（未登録）</NoWrapSpan>
        } else {
            return familyName && firstName ?
                <>
                    <NoWrapSpan>{familyName} </NoWrapSpan>
                    <NoWrapSpan>{firstName}</NoWrapSpan>
                </>
                :
                <NoWrapSpan>（退会済）</NoWrapSpan>
        }
    }
}
